/**
 * Nolana theme — overlay layer controller.
 * One controller for every off-canvas layer: mobile menu, search layer, cart drawer,
 * filter drawer. Ported and generalised from tema-demo/assets/js/nolana.js.
 *
 * Markup contract:
 *   <div id="MobileMenu" data-layer>…</div>        a layer (hidden until .is-open)
 *   [data-relocate] on a layer                     moved to <body> on init so a sticky/positioned
 *                                                 section wrapper cannot trap it in its stacking context
 *   [data-open-menu] / [data-open-search] / [data-open-cart] / [data-open-filter]
 *                                                 triggers (aria-expanded is kept in sync); links keep
 *                                                 their href as a fallback when the layer does not exist
 *   [data-close]                                  closes the open layer(s)
 *   [data-backdrop]                               shared dimming backdrop (also closes on click)
 *   [data-layer-focus]                            element to focus when the layer opens
 *
 * API: window.Nolana.layers.open(id) / close() / isOpen(id) / relocate()
 * Events: document "nolana:layer:open" / "nolana:layer:close" with {id}
 * Loaded once from layout/theme.liquid (never from a section, so Theme Editor reloads do not
 * duplicate the document-level listeners).
 */
(function () {
  "use strict";

  if (document.documentElement.dataset.nolanaLayersReady) return;
  document.documentElement.dataset.nolanaLayersReady = "true";

  var TRIGGERS = {
    "data-open-menu": "MobileMenu",
    "data-open-search": "SearchLayer",
    "data-open-cart": "CartDrawer",
    "data-open-filter": "FilterDrawer",
  };

  var current = null;
  var lastTrigger = null;

  function nolana() {
    return window.Nolana || {};
  }

  function backdrop() {
    return document.querySelector("[data-backdrop]");
  }

  /** Moves [data-relocate] layers out of section wrappers to the end of <body>. */
  function relocate(root) {
    (root || document).querySelectorAll("[data-layer][data-relocate]").forEach(function (layer) {
      if (layer.parentElement === document.body) return;
      var stale = document.body.querySelector(":scope > #" + CSS.escape(layer.id));
      if (stale && stale !== layer) stale.remove();
      document.body.appendChild(layer);
    });
  }

  function setTriggerState(id, expanded) {
    document.querySelectorAll('[aria-controls="' + id + '"]').forEach(function (trigger) {
      trigger.setAttribute("aria-expanded", expanded ? "true" : "false");
    });
  }

  function lockScroll(locked) {
    document.body.classList.toggle("is-layer-open", locked);
    document.documentElement.classList.toggle("is-layer-open", locked);
  }

  function close(returnFocus) {
    if (!current) return;
    var layer = current;
    current = null;
    layer.classList.remove("is-open");
    layer.setAttribute("aria-hidden", "true");
    setTriggerState(layer.id, false);
    lockScroll(false);
    var veil = backdrop();
    if (veil) veil.classList.remove("is-open");
    if (typeof nolana().releaseFocus === "function") {
      nolana().releaseFocus(returnFocus === false ? null : lastTrigger);
    } else if (returnFocus !== false && lastTrigger) {
      lastTrigger.focus();
    }
    lastTrigger = null;
    document.dispatchEvent(new CustomEvent("nolana:layer:close", { detail: { id: layer.id } }));
  }

  function open(id, trigger) {
    var layer = document.getElementById(id);
    if (!layer) return false;
    var previousTrigger = lastTrigger;
    var switching = !!(current && current !== layer);
    if (switching) close(false);
    current = layer;
    var candidate = trigger || document.activeElement;
    // A trigger inside the layer that just closed is hidden now; return focus to the original opener instead.
    lastTrigger = switching && candidate && candidate.closest && candidate.closest("[data-layer]") ? previousTrigger : candidate;
    layer.classList.add("is-open");
    layer.setAttribute("aria-hidden", "false");
    setTriggerState(id, true);
    lockScroll(true);
    var veil = backdrop();
    if (veil && layer.dataset.noBackdrop === undefined) veil.classList.add("is-open");

    var focusTarget = layer.querySelector("[data-layer-focus]") || layer.querySelector("[data-close]");
    // Synchronous focus keeps the user-gesture context so iOS Safari opens the keyboard for inputs.
    if (focusTarget) focusTarget.focus({ preventScroll: true });
    window.setTimeout(function () {
      if (typeof nolana().trapFocus === "function") nolana().trapFocus(layer, focusTarget);
    }, 60);

    document.dispatchEvent(new CustomEvent("nolana:layer:open", { detail: { id: id } }));
    return true;
  }

  function isOpen(id) {
    return !!current && (!id || current.id === id);
  }

  document.addEventListener("click", function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    for (var attribute in TRIGGERS) {
      var trigger = target.closest("[" + attribute + "]");
      if (trigger) {
        var id = trigger.getAttribute("aria-controls") || TRIGGERS[attribute];
        // A trigger that also closes (e.g. "Sepetim" inside the mobile menu) switches layers.
        // When the layer does not exist (e.g. cart type set to page) links fall through to their href.
        if (open(id, trigger)) event.preventDefault();
        else if (trigger.hasAttribute("data-close")) close();
        return;
      }
    }

    if (target.closest("[data-close]") || target.closest("[data-backdrop]")) {
      event.preventDefault();
      close();
    }
  });

  document.addEventListener("keydown", function (event) {
    if (event.key === "Escape" && current) {
      event.preventDefault();
      close();
    }
  });

  /* Search hint chips fill the input; predictive search (if loaded) reacts to the input event. */
  document.addEventListener("click", function (event) {
    var hint = event.target.closest("[data-search-hint]");
    if (!hint) return;
    var scope = hint.closest("[data-layer]") || document;
    var input = scope.querySelector("[data-search-input]");
    if (!input) return;
    input.value = hint.textContent.trim();
    input.focus();
    input.dispatchEvent(new Event("input", { bubbles: true }));
  });

  /* Theme Editor: show hidden layers while their section or block is selected. */
  if (window.Shopify && window.Shopify.designMode) {
    function layerFor(target) {
      if (!(target instanceof Element)) return null;
      return target.closest("[data-layer]") || target.querySelector("[data-layer]");
    }
    document.addEventListener("shopify:section:select", function (event) {
      var layer = layerFor(event.target);
      if (layer) open(layer.id);
    });
    document.addEventListener("shopify:block:select", function (event) {
      var layer = layerFor(event.target);
      if (layer && !isOpen(layer.id)) open(layer.id);
    });
    document.addEventListener("shopify:section:deselect", function (event) {
      var layer = layerFor(event.target);
      if (layer && isOpen(layer.id)) close(false);
    });
  }

  function init() {
    relocate(document);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", function (event) {
    relocate(event.target);
  });

  window.Nolana = window.Nolana || {};
  window.Nolana.layers = { open: open, close: close, isOpen: isOpen, relocate: relocate };
})();
